import { useChat, type UseChatHelpers } from "@ai-sdk/react";
import { toAISdkMessages } from "@mastra/ai-sdk/ui";
import { getMastraThreadLangfuseSessionId } from "@repo/app-types/entities/researchAgent";
import { checkIsAbortError } from "@repo/ui/helpers/errors";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import * as Sentry from "@sentry/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import { z } from "zod";
import { useLazyMastraThreadMessagesQuery } from "../../../api/entities/mastraThread.js";
import { getSessionAccessToken, mastraBaseUrl } from "../../../api/shared.js";

export interface MastraChatOptions {
  agentId: string;
  /** Local thread ID. Switching it resets local state and reloads history. */
  localThreadId?: string;
  /** Messages to pre-populate the chat with before any user interaction. */
  initialMessages?: UIMessage[];
  /**
   * Mastra thread to load history from. History reloads when `localThreadId`
   * changes; giving an existing local thread a remote counterpart (as happens
   * when the first message creates the thread) does not reload it.
   */
  loadThreadId?: string;
  /** Resolves per-send memory + request context (may create the thread). */
  onSend?: () => Promise<MastraChatSendInfo>;
}

export interface MastraChatSendInfo {
  /** Mastra memory thread. Omit for ephemeral (memory-less) chats. */
  thread?: { resourceId: string; threadId: string };
  /** Request context forwarded to the agent (e.g. playbookId). */
  requestContext?: Record<string, unknown>;
  onFinish?: () => void;
}

export interface MastraChat {
  messages: UIMessage[];
  /** Send a user text message and stream the agent's reply. */
  sendMessage: (text: string) => void;
  /**
   * Add messages without running the agent. A thread-backed chat sends them
   * with the next message, which saves them to the thread.
   */
  appendMessages: (messages: readonly UIMessage[]) => void;
  /** Abort the in-flight run. */
  stop: () => void;
  /** True while a run is submitting or streaming. */
  isRunning: boolean;
  /** True while loading thread history. */
  isLoading: boolean;
  setMessages: (
    messages: UIMessage[] | ((messages: UIMessage[]) => UIMessage[]),
  ) => void;
}

/**
 * Drives an admin agent chat through AI SDK UI's `useChat` against the backend
 * `chatRoute` (`POST {mastraBaseUrl}/chat/:agentId`). Replaces the
 * assistant-ui `useExternalStoreRuntime` + manual `@mastra/client-js`
 * streaming in `useMastraAgentRuntime`.
 */
export function useMastraChat(options: MastraChatOptions): MastraChat {
  const [defaultLocalThreadId] = useState(() => uuid());
  const { agentId, localThreadId = defaultLocalThreadId } = options;

  // Keep the latest options/derived values in a ref so the memoized transport's
  // `prepareSendMessagesRequest` always reads current values without re-creating
  // the transport (which would reset `useChat`).
  const sendRef = useRef<{
    onSend?: MastraChatOptions["onSend"];
    localThreadId: string;
    agentId: string;
    loadThreadId?: string;
  }>({
    onSend: options.onSend,
    localThreadId,
    agentId,
    loadThreadId: options.loadThreadId,
  });
  sendRef.current = {
    onSend: options.onSend,
    localThreadId,
    agentId,
    loadThreadId: options.loadThreadId,
  };

  // onFinish from the most recently resolved send, invoked when the run ends.
  const pendingFinishRef = useRef<(() => void) | undefined>(undefined);

  // The Mastra thread the in-flight send targets, cleared once the send settles
  // or the thread on screen changes. `loadThreadId` lags behind on a thread's
  // first message (the thread is created inside `onSend`), and cancelling
  // needs the thread id server-side: thread-backed runs are detached from the
  // request, so aborting the fetch alone no longer stops them.
  const activeSendThreadIdRef = useRef<string | undefined>(undefined);

  /** Forget the send's thread once it can no longer be stopped or reattached. */
  const unsetActiveSendThread = () => {
    activeSendThreadIdRef.current = undefined;
  };

  // Which kind of request `useChat` made last, so a dropped stream is only
  // reattached for a send and a failed reattach surfaces instead of retrying.
  const lastRequestKindRef = useRef<"send" | "reattach">("send");
  // Set when a send's stream drops mid-turn; its `onFinish` then reattaches.
  const shouldReattachRef = useRef(false);
  // Whether the reattach reached `onFinish` or `onError`. Neither fires when the
  // reattach endpoint answers 204 (no turn in flight).
  const didReattachSettleRef = useRef(false);

  // Appended messages memory doesn't have yet. Mastra saves a turn's messages
  // only once it finishes, so they stay unsent until a turn ends cleanly.
  const unsentMessageIdsRef = useRef(new Set<string>());

  const transport = useMemo(
    () =>
      new DefaultChatTransport<UIMessage>({
        api: `${mastraBaseUrl}/chat/${agentId}`,
        prepareSendMessagesRequest: async ({ messages }) => {
          lastRequestKindRef.current = "send";
          const send = await sendRef.current.onSend?.();
          pendingFinishRef.current = send?.onFinish;
          activeSendThreadIdRef.current = send?.thread?.threadId;

          const memory = send?.thread
            ? { resource: send.thread.resourceId, thread: send.thread.threadId }
            : undefined;

          // With server memory the agent owns history, so only the latest user
          // message is sent, with any appended messages memory doesn't have
          // yet; memory-less agents send the full local list.
          const outgoing = memory
            ? messages.filter(
                (message, index) =>
                  index === messages.length - 1 ||
                  unsentMessageIdsRef.current.has(message.id),
              )
            : messages;

          const langfuseSessionId = getLangfuseSessionId({
            agentId: sendRef.current.agentId,
            localThreadId: sendRef.current.localThreadId,
            thread: send?.thread,
          });

          const token = await getSessionAccessToken();
          return {
            headers: { Authorization: `Bearer ${token}` },
            body: {
              messages: outgoing,
              ...(memory ? { memory } : {}),
              requestContext: {
                ...(langfuseSessionId ? { langfuseSessionId } : {}),
                ...send?.requestContext,
              },
            },
          };
        },
        // `resumeStream` reattaches to a turn still running server-side (the
        // run outlives a refresh or disconnect). 204 means nothing in flight.
        // The caller names the thread in `body`: on a thread's first message
        // `loadThreadId` doesn't have it yet.
        prepareReconnectToStreamRequest: async ({ body }) => {
          lastRequestKindRef.current = "reattach";
          const threadId = reattachRequestBodySchema.parse(body).threadId;
          const token = await getSessionAccessToken();
          return {
            api: `${mastraBaseUrl}/chat-turns/${threadId}/stream`,
            headers: { Authorization: `Bearer ${token}` },
          };
        },
      }),
    [agentId],
  );

  // `onError` runs before `setMessages` is assigned below, so reach it via a
  // ref (its identity is stable; the ref is set on every render before any
  // error can fire).
  const setMessagesRef = useRef<MastraChat["setMessages"] | null>(null);

  // On a send/stream failure AI SDK only flips `status` to "error"; the chat
  // surfaces don't render that, so (matching the previous runtime) append a
  // visible assistant error message. The user's prompt is preserved, so they
  // can retry by resending.
  const appendErrorMessage = () => {
    setMessagesRef.current?.((prev) => [
      ...prev,
      {
        id: `error-${uuid()}`,
        role: "assistant",
        parts: [{ type: "text", text: "Something went wrong." }],
      },
    ]);
  };

  const messagesRef = useRef<readonly UIMessage[]>([]);

  const resumeStreamRef = useRef<
    UseChatHelpers<UIMessage>["resumeStream"] | null
  >(null);

  const [loadThreadMessages] = useLazyMastraThreadMessagesQuery();

  // Called after a dropped send when the server has no turn in flight any more.
  // That means one of two things: the turn finished while we were disconnected,
  // or it died. Mastra saves a turn's prompt and reply together, only once the
  // turn finishes, so the saved history tells the two apart.
  //
  // Returns the saved messages if the turn finished, otherwise null.
  const loadSavedMessagesIfTurnFinished = async (
    threadId: string,
  ): Promise<UIMessage[] | null> => {
    const { data } = await loadThreadMessages({
      agentId: sendRef.current.agentId,
      threadId,
    });
    if (!data) {
      return null;
    }
    const savedMessages: UIMessage[] = toAISdkMessages(
      structuredClone(data.messages),
      { version: "v6" },
    );

    // Counting prompts, because a saved message does not carry the id the
    // client gave it, so there is nothing to match ours against. Erring toward
    // "died" is the safe direction: it shows the error and keeps the reply on
    // screen, where erring the other way would replace a live chat with stale
    // history.
    const isPromptSaved =
      countUserMessages(savedMessages) >=
      countUserMessages(messagesRef.current);
    const endsWithReply = savedMessages.at(-1)?.role === "assistant";
    return isPromptSaved && endsWithReply ? savedMessages : null;
  };

  // Reattach to the turn whose stream dropped. The relay replays the turn from
  // its first chunk, so the partial assistant message is removed first;
  // otherwise the replay would append onto it and duplicate its parts.
  const reattachDroppedTurn = async (
    threadId: string,
    partialMessage: UIMessage,
  ) => {
    const droppedInLocalThreadId = sendRef.current.localThreadId;
    setMessagesRef.current?.((prev) =>
      prev.at(-1)?.id === partialMessage.id ? prev.slice(0, -1) : prev,
    );
    didReattachSettleRef.current = false;
    await resumeStreamRef.current?.({ body: { threadId } });
    if (didReattachSettleRef.current) {
      return;
    }

    // Nothing in flight (204): the turn finished during the gap, or it died.
    const finishedTurnMessages =
      await loadSavedMessagesIfTurnFinished(threadId);
    // `setMessages` writes to whichever thread is mounted now.
    if (sendRef.current.localThreadId !== droppedInLocalThreadId) {
      return;
    }
    if (finishedTurnMessages) {
      setMessagesRef.current?.(finishedTurnMessages);
    } else {
      // A send that failed before its first chunk has no partial reply.
      if (partialMessage.parts.length > 0) {
        setMessagesRef.current?.((prev) => [...prev, partialMessage]);
      }
      appendErrorMessage();
    }
    unsetActiveSendThread();
    pendingFinishRef.current?.();
    pendingFinishRef.current = undefined;
  };

  const { messages, sendMessage, stop, status, setMessages, resumeStream } =
    useChat<UIMessage>({
      id: localThreadId,
      messages: options.initialMessages,
      transport,
      onFinish: ({ message, isAbort, isError }) => {
        if (!isAbort && !isError) {
          unsentMessageIdsRef.current.clear();
        }
        const threadId = activeSendThreadIdRef.current;
        if (shouldReattachRef.current && threadId) {
          shouldReattachRef.current = false;
          reattachDroppedTurn(threadId, message).catch((error: unknown) =>
            Sentry.captureException(error),
          );
          return;
        }
        didReattachSettleRef.current = true;
        unsetActiveSendThread();
        pendingFinishRef.current?.();
        pendingFinishRef.current = undefined;
      },
      // Aborts (stop) don't reach here.
      onError: (error) => {
        // A dropped connection rejects the fetch or body read with a
        // `TypeError` in every browser; a thread-backed turn keeps running
        // server-side, so reattach (from `onFinish`, once this request has
        // wound down) instead of failing.
        if (
          error instanceof TypeError &&
          lastRequestKindRef.current === "send" &&
          activeSendThreadIdRef.current
        ) {
          shouldReattachRef.current = true;
          return;
        }
        didReattachSettleRef.current = true;
        unsetActiveSendThread();
        appendErrorMessage();
      },
    });
  setMessagesRef.current = setMessages;
  resumeStreamRef.current = resumeStream;
  messagesRef.current = messages;

  const appendMessages = useCallback(
    (newMessages: readonly UIMessage[]) => {
      newMessages.forEach((message) =>
        unsentMessageIdsRef.current.add(message.id),
      );
      setMessages((prev) => [...prev, ...newMessages]);
    },
    [setMessages],
  );

  // Load thread history whenever we switch threads.
  //
  // `isLoading` is derived rather than set from the effect: switching threads
  // swaps `useChat`'s state during the same render that changes
  // `localThreadId`, so a flag that only flips once the effect runs leaves one
  // painted frame with no messages and nothing loading — long enough for the
  // empty "new chat" state to flash between two existing threads.
  const { loadThreadId } = options;
  const [loadedLocalThreadId, setLoadedLocalThreadId] = useState<string>();
  const isLoading =
    loadThreadId !== undefined && loadedLocalThreadId !== localThreadId;
  const onLoad = useEffectEvent(async (signal: AbortSignal) => {
    try {
      if (loadThreadId === undefined) {
        return;
      }

      // Not `.unwrap()`: it rejects an aborted request with a plain `Error` that
      // `checkIsAbortError` can't tell from a real failure, whereas the promise
      // itself resolves and lets the abort check below run first.
      const request = loadThreadMessages({ agentId, threadId: loadThreadId });
      signal.addEventListener("abort", () => request.abort());
      const { data, error } = await request;

      // `setMessages` always writes to whichever thread is mounted now, so a
      // superseded load would drop its history into the thread that replaced
      // it.
      if (signal.aborted) {
        return;
      }
      if (error || !data) {
        throw new Error(
          `Failed to load chat history: ${JSON.stringify(error)}`,
        );
      }

      // `toAISdkMessages` feeds a Mastra `MessageList`, which stamps `createdAt`
      // onto the messages it is handed. RTK Query's cache is frozen, so it has to
      // get a copy -- the pre-RTK fetch happened to pass freshly parsed JSON.
      setMessages(
        toAISdkMessages(structuredClone(data.messages), { version: "v6" }),
      );

      // Reattach to a turn still running server-side (the run survives a
      // refresh or disconnect): the transport probes the thread's reattach
      // endpoint, which replays the turn's chunks and tails; a 204 (nothing in
      // flight) is a silent no-op. Reconnect failures surface through
      // `onError` like any stream failure.
      resumeStream({ body: { threadId: loadThreadId } }).catch(
        (error: unknown) => Sentry.captureException(error),
      );
    } finally {
      // A superseded load must leave `loadedLocalThreadId` alone: it is what
      // holds `isLoading` false, and the effect only runs once per thread, so
      // stamping the abandoned thread's ID over the current one's would strand
      // the skeleton forever, and an aborted load still unwinds through here
      // after its replacement has started.
      //
      // Threads with no history to load still get marked, so `isLoading` stays
      // false once such a thread gains a remote counterpart of its own.
      if (!signal.aborted) {
        setLoadedLocalThreadId(localThreadId);
      }
    }
  });

  useEffect(() => {
    // A send belongs to the thread it started in; stop() must not reach back
    // to it from the thread now on screen.
    unsetActiveSendThread();
    shouldReattachRef.current = false;

    const abortController = new AbortController();
    onLoad(abortController.signal).catch((error: unknown) => {
      if (checkIsAbortError(error)) {
        return;
      }
      throw error;
    });
    return () => abortController.abort();
  }, [agentId, localThreadId, onLoad]);

  const sendTextMessage = useCallback(
    (text: string) => sendMessage({ text }),
    [sendMessage],
  );

  return {
    messages,
    sendMessage: sendTextMessage,
    appendMessages,
    // Aborting the fetch only detaches the client; a thread-backed run keeps
    // going server-side until the cancel endpoint deletes its active-turn
    // pointer. Best-effort: a failed cancel just lets the turn run to
    // completion.
    stop: () => {
      // Read before aborting: the abort settles the send, which clears its id.
      const threadId =
        activeSendThreadIdRef.current ?? sendRef.current.loadThreadId;
      stop();
      if (threadId) {
        cancelChatTurn(threadId).catch((error: unknown) =>
          Sentry.captureException(error),
        );
      }
    },
    isRunning: status === "submitted" || status === "streaming",
    isLoading,
    setMessages,
  };
}

const reattachRequestBodySchema = z.object({ threadId: z.string() });

function countUserMessages(messages: readonly UIMessage[]): number {
  return messages.filter((message) => message.role === "user").length;
}

async function cancelChatTurn(threadId: string): Promise<void> {
  const token = await getSessionAccessToken();
  await fetch(`${mastraBaseUrl}/chat-turns/${threadId}/cancel`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
}

function getLangfuseSessionId(options: {
  agentId: string;
  localThreadId: string | undefined;
  thread: { threadId: string } | undefined;
}) {
  if (options.thread) {
    return getMastraThreadLangfuseSessionId(
      options.agentId,
      options.thread.threadId,
    );
  }
  if (options.localThreadId) {
    return `${options.agentId}_client_thread_${options.localThreadId}`;
  }
  return undefined;
}
